//////////////////////////////////////////////////////////////////////////////
//
//  푸시 발송 이력 목록 화면
//
//  작성자: MediInStep Team
//  생성일: 2025-12-29
//  수정일: 2026-01-11 (Standardized UI)
//
//  설명: 전체 푸시 발송 이력 조회 및 관리 화면 (GenericListScreen 적용)
//
//////////////////////////////////////////////////////////////////////////////

import React, { useMemo } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Chip from "@mui/material/Chip";
import { formatLocalDateTime, formatLocalDate, formatLocalTime } from "utils/dateFormatter";
import GenericListScreen from "components/GenericListScreen/GenericListScreen";

// Helper for status chips
const getStatusChip = (history: any) => {
    if (history.readDt) {
        return <Chip label="읽음" color="success" size="small" />;
    } else if (history.deliveredDt) {
        return <Chip label="전달됨" color="info" size="small" />;
    } else if (history.sentDt) {
        return <Chip label="발송됨" color="primary" size="small" />;
    } else if (history.errorCode) {
        return <Chip label="실패" color="error" size="small" />;
    } else {
        return <Chip label="대기" color="default" size="small" />;
    }
};

function PushHistoryScreen() {
    // Context / Storage for Tenant
    const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
    const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);
    const tenantSeqId = savedTenantId ? parseInt(savedTenantId, 10) : null;

    // Extra parameters for the API query
    const extraParams = useMemo(() => ({
        TenantSeqId: tenantSeqId
    }), [tenantSeqId]);

    const renderDateCell = (value: string | null) => {
        if (!value || value === "-") return "-";

        return (
            <MDBox lineHeight={1.2} textAlign="center">
                <MDTypography variant="caption" display="block" color="text" fontWeight="medium">
                    {formatLocalDate(value)}
                </MDTypography>
                <MDTypography variant="caption" display="block" color="text">
                    {formatLocalTime(value)}
                </MDTypography>
            </MDBox>
        );
    };

    // Column Definitions
    const columns = useMemo(() => [
        {
            Header: "사용자",
            accessor: "userName",
            width: "10%",
            Cell: ({ row }: any) => {
                const { userName, userSeqId } = row.original;
                return userName || `User #${userSeqId || "N/A"}`;
            }
        },
        {
            Header: "캠페인",
            accessor: "campaignName",
            width: "12%",
            Cell: ({ value }: any) => value || "-"
        },
        {
            Header: "제목",
            accessor: "title",
            width: "20%"
        },
        {
            Header: "상태",
            accessor: "status", // Virtual accessor for sorting if needed, but we use Cell for display
            width: "8%",
            Cell: ({ row }: any) => getStatusChip(row.original)
        },
        {
            Header: "발송 시간",
            accessor: "sentDt",
            width: "12%",
            Cell: ({ value }: any) => renderDateCell(value)
        },
        {
            Header: "전달 시간",
            accessor: "deliveredDt",
            width: "12%",
            Cell: ({ value }: any) => renderDateCell(value)
        },
        {
            Header: "읽음 시간",
            accessor: "readDt",
            width: "12%",
            Cell: ({ value }: any) => renderDateCell(value)
        },
        {
            Header: "오류",
            accessor: "errorCode",
            width: "14%",
            Cell: ({ row }: any) => {
                const { errorCode, errorMessage } = row.original;
                return errorCode ? (
                    <MDTypography variant="caption" color="error" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {errorCode}: {errorMessage}
                    </MDTypography>
                ) : "-";
            }
        }
    ], []);

    return (
        <GenericListScreen
            title="발송 이력 ( PUSH HISTORY )"
            icon="notifications"
            apiName="PushHistory.pagedList"
            columns={columns}
            extraParams={extraParams}
            defaultPageSize={20}
        />
    );
}

export default PushHistoryScreen;
